import editionsJson from "./newsEditions.json";
import sourcesJson from "./newsSources.json";

export type NewsTreatment = "video-spot" | "glance";
export type NewsDecision = "keep" | "keep-selective" | "watch-selective" | "glance-only";
export type NewsTopic = "world" | "china" | "business" | "tech" | "science" | "health" | "culture";

export type NewsSourceLink = {
  name: string;
  url: string;
  favorite?: boolean;
};

export type NewsVideo = {
  src: string;
  poster?: string;
  duration?: string;
};

export type NewsTopItem = {
  id: string;
  kind: "video" | "read";
  topic: NewsTopic;
  minutes: number;
  title: string;
  points: string[];
  more: string[];
  video?: NewsVideo;
  source: NewsSourceLink;
};

export type NewsGlanceItem = {
  id: string;
  topic: NewsTopic;
  minutes: number;
  title: string;
  points: string[];
  source: NewsSourceLink;
};

export type NewsEdition = {
  date: string;
  top: NewsTopItem[];
  glance: NewsGlanceItem[];
};

export type NewsDirectorySource = {
  id: string;
  name: string;
  platform: "youtube" | "bilibili";
  url: string;
  niche: string;
  cluster: string;
  decision: NewsDecision;
  treatment: NewsTreatment;
  why: string;
  gate?: string;
  priority?: number;
  origin: "roster" | "seed";
};

type NewsSourceDirectory = {
  audit: string;
  skipped: number;
  sources: NewsDirectorySource[];
};

// Reader time is the scarce resource: three is the format, five is the ceiling for glances.
export const newsLimits = { top: 3, videos: 3, points: 3, glance: 5 } as const;

export const topicLabels: Record<NewsTopic, string> = {
  world: "World",
  china: "China",
  business: "Business",
  tech: "Tech & AI",
  science: "Science",
  health: "Health",
  culture: "Culture",
};

export const weeklyTopics = Object.keys(topicLabels) as NewsTopic[];

export const platformLabels: Record<NewsDirectorySource["platform"], string> = {
  youtube: "YouTube",
  bilibili: "Bilibili",
};

export const decisionLabels: Record<NewsDecision, string> = {
  keep: "Keep",
  "keep-selective": "Keep selective",
  "watch-selective": "Watch selective",
  "glance-only": "Glance only",
};

export const treatmentGroups: { treatment: NewsTreatment; title: string; description: string }[] = [
  { treatment: "video-spot", title: "Video spot", description: "Can take the day’s video slot. One a day at most." },
  { treatment: "glance", title: "Glance", description: "A line or a transcript first. Never a full slot." },
];

export const nicheLabel = (niche: string) => niche.replace(/-/g, " ");

const shapeEdition = (edition: NewsEdition): NewsEdition => {
  const favoritesFirst = [...edition.top].sort(
    (left, right) => Number(Boolean(right.source.favorite)) - Number(Boolean(left.source.favorite)),
  );
  let videos = 0;
  const top = favoritesFirst.slice(0, newsLimits.top).map((item) => {
    const video = item.video && videos < newsLimits.videos ? item.video : undefined;
    if (video) videos += 1;
    return { ...item, video, points: item.points.slice(0, newsLimits.points) };
  });
  const glance = edition.glance
    .slice(0, newsLimits.glance)
    .map((item) => ({ ...item, points: item.points.slice(0, newsLimits.points) }));
  return { ...edition, top, glance };
};

export const editions = (editionsJson as NewsEdition[])
  .map(shapeEdition)
  .sort((left, right) => (left.date < right.date ? 1 : left.date > right.date ? -1 : 0));

const directory = sourcesJson as NewsSourceDirectory;
export const directoryAudit = directory.audit;
export const skippedSourceCount = directory.skipped;
export const directorySources = directory.sources;
export const sourcesByTreatment = (treatment: NewsTreatment) =>
  directorySources.filter((source) => source.treatment === treatment);

export const isEditionDate = (value: string | undefined): value is string =>
  Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));

export const latestEdition = () => editions[0];

export const editionByDate = (date: string | undefined) =>
  isEditionDate(date) ? editions.find((edition) => edition.date === date) : undefined;

export const editionStats = (edition: NewsEdition) => {
  const sources = new Set([...edition.top, ...edition.glance].map((item) => item.source.name));
  const minutes = [...edition.top, ...edition.glance].reduce((total, item) => total + item.minutes, 0);
  return {
    minutes,
    sources: sources.size,
    videos: edition.top.filter((item) => item.video).length,
  };
};

const weekStart = (date: string) => {
  const day = new Date(`${date}T00:00:00Z`);
  const offset = (day.getUTCDay() + 6) % 7;
  day.setUTCDate(day.getUTCDate() - offset);
  return day.toISOString().slice(0, 10);
};

export const weeklyCoverage = (edition: NewsEdition) => {
  const start = weekStart(edition.date);
  const covered = new Set<NewsTopic>();
  editions
    .filter((item) => item.date <= edition.date && weekStart(item.date) === start)
    .forEach((item) => [...item.top, ...item.glance].forEach((entry) => covered.add(entry.topic)));
  return weeklyTopics.map((topic) => ({ topic, label: topicLabels[topic], covered: covered.has(topic) }));
};

export const plainText = (text: string) => text.replace(/==([^=]+)==/g, "$1");
